import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import DateInputWithArrows from '../components/DateInputWithArrows';
import CostSheetTreeView from '../components/CostSheetTreeView';
import { formatDateEuropean } from '../utils/dateUtils';
import { 
  getProjects, 
  getWorkQuantitiesForProject, 
  bulkCreateWorkQuantities,
  getProjectCostSheets,
  getProjectCostSheetCategories,
  deleteWorkQuantity
} from '../services/api';
import { useNotification } from '../context/NotificationContext';
import useResponsive from '../components/responsive/useResponsive';
import ResponsiveDataList from '../components/responsive/ResponsiveDataList';
import { useTranslation } from 'react-i18next';

function WorkQuantities() {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { showNotification } = useNotification();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [costSheets, setCostSheets] = useState([]);
  const [costSheetCategories, setCostSheetCategories] = useState([]);
  const [allProjectCostSheets, setAllProjectCostSheets] = useState([]);
  const [workQuantities, setWorkQuantities] = useState({});
  const [notes, setNotes] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingMode, setEditingMode] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, workQuantityId: null });
  const [addCostSheetDialog, setAddCostSheetDialog] = useState(false);

  // Load projects on component mount
  useEffect(() => {
    loadProjects();
  }, []);

  // Auto-load work quantities when project and date range are selected
  useEffect(() => {
    if (selectedProject && fromDate && toDate) {
      loadWorkQuantities();
    }
  }, [selectedProject, fromDate, toDate]);

  const loadProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
    } catch (error) {
      console.error('Error loading projects:', error);
      showNotification(t('notifications.errorLoadingProjects', { defaultValue: 'Error loading projects' }), 'error');
    }
  };

  const loadWorkQuantities = async () => {
    if (!selectedProject || !fromDate || !toDate) {
      return;
    }

    setLoading(true);
    try {
      const response = await getWorkQuantitiesForProject(
        selectedProject,
        fromDate.toISOString().split('T')[0],
        toDate.toISOString().split('T')[0]
      );
      
      const workQuantitiesData = response.data;
      
      // Transform work quantities data to cost sheets format for compatibility
      const costSheetsData = workQuantitiesData.map(workQuantity => ({
        ...workQuantity.costSheet,
        workQuantities: [workQuantity], // Wrap in array for compatibility
        workQuantityId: workQuantity.id // Store the work quantity ID for updates
      }));
      
      setCostSheets(costSheetsData);
      
      // Initialize work quantities and notes from existing data
      const initialQuantities = {};
      const initialNotes = {};
      
      costSheetsData.forEach(costSheet => {
        const workQuantity = costSheet.workQuantities[0];
        // Use workQuantityId as key to avoid conflicts
        initialQuantities[costSheet.workQuantityId] = workQuantity.quantity;
        initialNotes[costSheet.workQuantityId] = workQuantity.notes || '';
      });
      
      setWorkQuantities(initialQuantities);
      setNotes(initialNotes);
    } catch (error) {
      console.error('Error loading work quantities:', error);
      showNotification(t('notifications.errorLoadingWorkQuantities', { defaultValue: 'Error loading work quantities' }), 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadProjectCategories = async (projectId) => {
    try {
      const [categoriesRes, costSheetsRes] = await Promise.all([
        getProjectCostSheetCategories(projectId),
        getProjectCostSheets(projectId)
      ]);
      setCostSheetCategories(categoriesRes.data || []);
      setAllProjectCostSheets(costSheetsRes.data || []);
    } catch (error) {
      console.error('Error loading project data:', error);
      showNotification(t('notifications.errorLoadingProjectData', { defaultValue: 'Error loading project data' }), 'error');
    }
  };

  const handleProjectChange = (event) => {
    const projectId = event.target.value;
    setSelectedProject(projectId);
    setCostSheets([]);
    setWorkQuantities({});
    setNotes({});
    
    // Load project categories for cost sheet selection
    if (projectId) {
      loadProjectCategories(projectId);
    }
  };

  const handleDateChange = (date, type) => {
    if (type === 'from') {
      setFromDate(date);
    } else {
      setToDate(date);
    }
    setCostSheets([]);
    setWorkQuantities({});
    setNotes({});
  };

  const handleQuantityChange = (workQuantityId, value) => {
    setWorkQuantities(prev => ({
      ...prev,
      [workQuantityId]: value
    }));
  };

  const handleNotesChange = (workQuantityId, value) => {
    setNotes(prev => ({
      ...prev,
      [workQuantityId]: value
    }));
  };

  const handleSave = async () => {
    if (!selectedProject || !fromDate || !toDate) {
      showNotification(t('validation.selectProjectDateRange', { defaultValue: 'Please select project and date range' }), 'error');
      return;
    }

    setSaving(true);
    try {
      const workQuantitiesData = costSheets
        .filter(costSheet => {
          const quantity = workQuantities[costSheet.workQuantityId];
          return quantity !== '' && quantity !== null && quantity !== undefined;
        })
        .map(costSheet => ({
          workQuantityId: String(costSheet.workQuantityId).startsWith('temp-') ? null : costSheet.workQuantityId, // Only include ID for existing records
          costSheetId: costSheet.id,
          quantity: parseFloat(workQuantities[costSheet.workQuantityId]),
          notes: notes[costSheet.workQuantityId] || null
        }));

      if (workQuantitiesData.length === 0) {
        showNotification(t('validation.enterOneWorkQuantity', { defaultValue: 'Please enter at least one work quantity' }), 'error');
        return;
      }

      await bulkCreateWorkQuantities({
        projectId: parseInt(selectedProject),
        fromDate: fromDate.toISOString().split('T')[0],
        toDate: toDate.toISOString().split('T')[0],
        workQuantities: workQuantitiesData
      });

      showNotification(t('notifications.workQuantitiesSaved', { defaultValue: 'Work quantities saved successfully' }), 'success');
      setEditingMode(false);
      loadWorkQuantities(); // Reload to show updated data
    } catch (error) {
      console.error('Error saving work quantities:', error);
      showNotification(t('notifications.errorSavingWorkQuantities', { defaultValue: 'Error saving work quantities' }), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (workQuantityId) => {
    try {
      // Delete from database
      await deleteWorkQuantity(workQuantityId);
      
      // Remove from local state
      setCostSheets(prev => prev.filter(cs => cs.workQuantityId !== workQuantityId));
      setWorkQuantities(prev => {
        const updated = { ...prev };
        delete updated[workQuantityId];
        return updated;
      });
      setNotes(prev => {
        const updated = { ...prev };
        delete updated[workQuantityId];
        return updated;
      });
      
      showNotification(t('notifications.workQuantityDeleted', { defaultValue: 'Work quantity deleted successfully' }), 'success');
    } catch (error) {
      console.error('Error deleting work quantity:', error);
      showNotification(t('notifications.errorDeletingWorkQuantity', { defaultValue: 'Error deleting work quantity' }), 'error');
    }
    setDeleteDialog({ open: false, workQuantityId: null });
  };

  const handleAddCostSheet = (costSheet) => {
    // Add the cost sheet to the list if it's not already there
    const existingCostSheet = costSheets.find(cs => cs.id === costSheet.id);
    if (!existingCostSheet) {
      // Create a temporary work quantity entry for new cost sheets
      const tempWorkQuantityId = `temp-${costSheet.id}-${Date.now()}`;
      const newCostSheetEntry = {
        ...costSheet,
        workQuantityId: tempWorkQuantityId,
        workQuantities: [{
          id: tempWorkQuantityId,
          quantity: '',
          notes: '',
          fromDate: fromDate,
          toDate: toDate
        }]
      };
      
      setCostSheets(prev => [...prev, newCostSheetEntry]);
      setWorkQuantities(prev => ({
        ...prev,
        [tempWorkQuantityId]: ''
      }));
      setNotes(prev => ({
        ...prev,
        [tempWorkQuantityId]: ''
      }));
      showNotification(t('notifications.costSheetAdded', { defaultValue: `Added ${costSheet.name} to work quantities` }), 'success');
    } else {
      showNotification(t('notifications.costSheetAlreadyAdded', { defaultValue: 'Cost sheet already added' }), 'warning');
    }
    setAddCostSheetDialog(false);
  };

  const removeCostSheet = (workQuantityId) => {
    setCostSheets(prev => prev.filter(cs => cs.workQuantityId !== workQuantityId));
    setWorkQuantities(prev => {
      const updated = { ...prev };
      delete updated[workQuantityId];
      return updated;
    });
    setNotes(prev => {
      const updated = { ...prev };
      delete updated[workQuantityId];
      return updated;
    });
  };

  const canAddCostSheet = selectedProject && fromDate && toDate;
  const hasWorkQuantities = Object.values(workQuantities).some(qty => qty !== '' && qty !== null && qty !== undefined);
  const hasCostSheets = costSheets.length > 0;

  return (
    <Box sx={{ p: { xs: 0, md: 3 } }}>
      <Typography variant="h4" gutterBottom>
        {t('nav.workQuantities')}
      </Typography>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label={t('projects.title')}
                value={selectedProject}
                onChange={handleProjectChange}
                variant="outlined"
              >
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <DateInputWithArrows
                fullWidth
                label={t('personnel.fields.startDate')}
                value={fromDate ? fromDate.toISOString().split('T')[0] : ''}
                onChange={(e) => handleDateChange(new Date(e.target.value), 'from')}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <DateInputWithArrows
                fullWidth
                label={t('personnel.fields.endDate')}
                value={toDate ? toDate.toISOString().split('T')[0] : ''}
                onChange={(e) => handleDateChange(new Date(e.target.value), 'to')}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                onClick={() => setAddCostSheetDialog(true)}
                disabled={!canAddCostSheet}
                fullWidth
                startIcon={<AddIcon />}
              >
                {t('costSheetsPage.add')}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      )}

      {costSheets.length > 0 && (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
            <Typography variant="h6">
              Work Quantities ({costSheets.length})
            </Typography>
              <Box>
                {!editingMode ? (
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => setEditingMode(true)}
                    disabled={!hasCostSheets}
                  >
                    {hasWorkQuantities ? 'Edit' : 'Enter Work Quantities'}
                  </Button>
                ) : (
                  <Box>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSave}
                      disabled={saving}
                      sx={{ mr: 1 }}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={() => setEditingMode(false)}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>

            {isMobile ? (
              <ResponsiveDataList
                rows={costSheets}
                getTitle={(costSheet) => `${costSheet.code || '-'} ${costSheet.name}`}
                getSubtitle={(costSheet) => costSheet.category?.name || 'Uncategorized'}
                renderFields={(costSheet) => {
                  const hasQuantity = workQuantities[costSheet.workQuantityId] !== '' &&
                    workQuantities[costSheet.workQuantityId] !== null &&
                    workQuantities[costSheet.workQuantityId] !== undefined;
                  return (
                    <Box>
                      <Typography variant="body2"><b>Unit:</b> {costSheet.unit}</Typography>
                      {editingMode ? (
                        <Box sx={{ mt: 1 }}>
                          <TextField
                            fullWidth
                            type="number"
                            value={workQuantities[costSheet.workQuantityId] ?? ''}
                            onChange={(e) => handleQuantityChange(costSheet.workQuantityId, e.target.value)}
                            label="Work Quantity"
                            size="small"
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                          <TextField
                            fullWidth
                            value={notes[costSheet.workQuantityId] || ''}
                            onChange={(e) => handleNotesChange(costSheet.workQuantityId, e.target.value)}
                            label={t('personnel.fields.notes')}
                            size="small"
                            multiline
                            rows={2}
                            sx={{ mt: 1 }}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2"><b>Quantity:</b> {hasQuantity ? `${workQuantities[costSheet.workQuantityId]} ${costSheet.unit}` : 'Not set'}</Typography>
                      )}
                    </Box>
                  );
                }}
                renderActions={(costSheet) => editingMode ? (
                  <Button size="small" color="error" onClick={() => setDeleteDialog({ open: true, workQuantityId: costSheet.workQuantityId })}>
                    Remove
                  </Button>
                ) : null}
              />
            ) : (
            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell>Date Range</TableCell>
                    <TableCell>Work Quantity</TableCell>
                    <TableCell>Notes</TableCell>
                    {editingMode && <TableCell>Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {costSheets.map((costSheet) => {
                    const hasQuantity = workQuantities[costSheet.workQuantityId] !== '' && 
                                      workQuantities[costSheet.workQuantityId] !== null && 
                                      workQuantities[costSheet.workQuantityId] !== undefined;
                    
                    return (
                      <TableRow key={costSheet.workQuantityId}>
                        <TableCell>{costSheet.code || '-'}</TableCell>
                        <TableCell>{costSheet.name}</TableCell>
                        <TableCell>
                          <Chip 
                            label={costSheet.category?.name || 'Uncategorized'} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{costSheet.unit}</TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {costSheet.workQuantities && costSheet.workQuantities[0] ? (
                              `${formatDateEuropean(costSheet.workQuantities[0].fromDate)} - ${formatDateEuropean(costSheet.workQuantities[0].toDate)}`
                            ) : (
                              'No date range'
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {editingMode ? (
                            <TextField
                              type="number"
                              value={workQuantities[costSheet.workQuantityId] ?? ''}
                              onChange={(e) => handleQuantityChange(costSheet.workQuantityId, e.target.value)}
                              placeholder="Enter quantity"
                              size="small"
                              sx={{ width: 120 }}
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                          ) : (
                            <Box display="flex" alignItems="center">
                              {hasQuantity ? (
                                <Typography variant="body2">
                                  {workQuantities[costSheet.workQuantityId]} {costSheet.unit}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  Not set
                                </Typography>
                              )}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          {editingMode ? (
                            <TextField
                              value={notes[costSheet.workQuantityId] || ''}
                              onChange={(e) => handleNotesChange(costSheet.workQuantityId, e.target.value)}
                              placeholder="Optional notes"
                              size="small"
                              multiline
                              rows={1}
                              sx={{ width: 200 }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {notes[costSheet.workQuantityId] || '-'}
                            </Typography>
                          )}
                        </TableCell>
                        {editingMode && (
                          <TableCell>
                            <Tooltip title="Remove work quantity">
                              <IconButton
                                size="small"
                                onClick={() => setDeleteDialog({ open: true, workQuantityId: costSheet.workQuantityId })}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            )}

            {costSheets.length === 0 && !loading && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No work quantities found for the selected project and date range. 
                {t('workQuantities.emptyHint', { defaultValue: 'Click "Add Cost Sheet" to add cost sheets with approved work ready to be billed.' })}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, workQuantityId: null })}
          fullScreen={isMobile}
        >
          <DialogTitle>Delete Work Quantity</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this work quantity? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({ open: false, workQuantityId: null })}>
              Cancel
            </Button>
            <Button onClick={() => handleDelete(deleteDialog.workQuantityId)} color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

      {/* Add Cost Sheet Dialog */}
      <Dialog
        open={addCostSheetDialog}
        onClose={() => setAddCostSheetDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Add Cost Sheet</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Select a cost sheet that has approved work ready to be billed:
          </Typography>
          <Box mt={2}>
            <CostSheetTreeView
              categories={costSheetCategories}
              onCostSheetSelect={handleAddCostSheet}
              selectedCostSheetId={null}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCostSheetDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default WorkQuantities;